import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import React, { useState, useEffect } from 'react';

const InstallPWAPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [debugInfo, setDebugInfo] = useState({});

    useEffect(() => {
        const handler = (e) => {
            console.log("beforeinstallprompt event fired");
            console.log("Event object:", e);
            console.log("Event type:", typeof e);
            console.log("Event constructor:", e.constructor.name);
            console.log("Has prompt method:", typeof e.prompt === 'function');
            console.log("Has userChoice:", 'userChoice' in e);
            console.log("Event properties:", Object.getOwnPropertyNames(e));

            // Validate that this is a proper BeforeInstallPromptEvent
            if (typeof e.prompt !== 'function') {
                console.error("Invalid beforeinstallprompt event - missing prompt method");
                console.log("This might be a custom event or browser compatibility issue");
                return;
            }

            e.preventDefault();
            setDeferredPrompt(e);
            setShowPrompt(true);

            // Store debug info
            setDebugInfo({
                hasPrompt: typeof e.prompt === 'function',
                hasUserChoice: 'userChoice' in e,
                eventType: e.constructor.name,
                timestamp: new Date().toISOString()
            });
        };

        const appInstalledHandler = () => {
            console.log("App was installed");
            setShowPrompt(false);
            setDeferredPrompt(null);
        };

        // Check PWA requirements
        const checkPWARequirements = () => {
            const requirements = {
                isHTTPS: location.protocol === 'https:' || location.hostname === 'localhost',
                hasServiceWorker: 'serviceWorker' in navigator,
                hasManifest: !!document.querySelector('link[rel="manifest"]'),
                supportsBeforeInstall: 'onbeforeinstallprompt' in window,
                isStandalone: window.matchMedia('(display-mode: standalone)').matches
            };

            console.log("PWA Requirements Check:", requirements);

            const allMet = Object.values(requirements).every(req => req === true);
            if (!allMet) {
                console.warn("Not all PWA requirements are met:", requirements);
            }

            return requirements;
        };

        checkPWARequirements();

        window.addEventListener('beforeinstallprompt', handler);
        window.addEventListener('appinstalled', appInstalledHandler);

        // Timeout to check if event fired
        const timeoutId = setTimeout(() => {
            if (!deferredPrompt) {
                console.log("beforeinstallprompt event did not fire within 3 seconds");
                console.log("Possible reasons:");
                console.log("1. App is already installed");
                console.log("2. User has dismissed the prompt multiple times");
                console.log("3. PWA requirements not fully met");
                console.log("4. Browser doesn't support PWA installation");
            }
        }, 3000);

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
            window.removeEventListener('appinstalled', appInstalledHandler);
            clearTimeout(timeoutId);
        };
    }, []);

    const handleInstall = async () => {
        console.log("Install button clicked");
        console.log("Deferred prompt:", deferredPrompt);

        if (!deferredPrompt) {
            console.error("No deferred prompt available");
            alert("Installation prompt is not available. Please try refreshing the page or install manually from your browser menu.");
            return;
        }

        // Validate prompt method before calling
        if (typeof deferredPrompt.prompt !== 'function') {
            console.error("prompt method is not available on the deferred prompt object");
            console.log("Deferred prompt type:", typeof deferredPrompt);
            console.log("Deferred prompt constructor:", deferredPrompt.constructor.name);
            console.log("Available methods:", Object.getOwnPropertyNames(deferredPrompt));

            alert("Installation method is not supported. Please install manually:\n" +
                "1. Click the browser menu (⋮)\n" +
                "2. Look for 'Install app' or 'Add to Home Screen'\n" +
                "3. Follow the prompts");
            return;
        }

        try {
            console.log("Calling deferredPrompt.prompt()...");

            // Call the prompt method
            await deferredPrompt.prompt();

            console.log("Prompt displayed successfully");

            // Wait for user choice if available
            if (deferredPrompt.userChoice) {
                const { outcome } = await deferredPrompt.userChoice;
                console.log("User response to install prompt:", outcome);

                if (outcome === 'accepted') {
                    console.log('User accepted the install prompt');
                    localStorage.setItem('pwa-install-accepted', 'true');
                } else {
                    console.log('User dismissed the install prompt');
                    localStorage.setItem('pwa-install-dismissed', 'true');
                }
            } else {
                console.log("userChoice not available");
            }

            // Clean up
            setShowPrompt(false);
            setDeferredPrompt(null);

        } catch (error) {
            console.error("Error during installation:", error);
            console.error("Error name:", error.name);
            console.error("Error message:", error.message);
            console.error("Error stack:", error.stack);

            // Provide user-friendly error message
            let errorMessage = "Installation failed. ";

            if (error.name === 'TypeError' && error.message.includes('prompt')) {
                errorMessage += "The installation prompt is not supported in this browser or context.";
            } else if (error.name === 'InvalidStateError') {
                errorMessage += "The installation prompt has already been used.";
            } else if (error.name === 'NotAllowedError') {
                errorMessage += "Installation was blocked by the browser.";
            } else {
                errorMessage += "Please try installing manually from your browser menu.";
            }

            alert(errorMessage + "\n\nManual installation:\n" +
                "1. Click the browser menu (⋮)\n" +
                "2. Look for 'Install app' or 'Add to Home Screen'\n" +
                "3. Follow the prompts");
        }
    };

    // Debug function to test with mock event
    const testWithMockEvent = () => {
        console.log("Testing with mock beforeinstallprompt event");

        const mockEvent = {
            preventDefault: () => console.log("Mock preventDefault called"),
            prompt: async () => {
                console.log("Mock prompt called");
                return new Promise((resolve) => {
                    setTimeout(() => {
                        console.log("Mock prompt resolved");
                        resolve();
                    }, 1000);
                });
            },
            userChoice: new Promise((resolve) => {
                setTimeout(() => {
                    const outcome = Math.random() > 0.5 ? 'accepted' : 'dismissed';
                    console.log("Mock user choice:", outcome);
                    resolve({ outcome });
                }, 2000);
            })
        };

        setDeferredPrompt(mockEvent);
        setShowPrompt(true);
    };

    // Function to clear PWA data for testing
    const clearPWAData = () => {
        localStorage.removeItem('pwa-install-accepted');
        localStorage.removeItem('pwa-install-dismissed');
        console.log("Cleared PWA installation data");
    };

    if (!showPrompt) {
        return (
            <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
                {/* Debug panel for development */}
                {process.env.NODE_ENV === 'development' && (
                    <div style={{
                        background: 'white',
                        border: '1px solid #ccc',
                        padding: '10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginBottom: '10px',
                        maxWidth: '300px'
                    }}>
                        <strong>PWA Debug Info:</strong><br/>
                        Show Prompt: {showPrompt.toString()}<br/>
                        Has Deferred Prompt: {(!!deferredPrompt).toString()}<br/>
                        Debug Info: {JSON.stringify(debugInfo, null, 2)}<br/>
                        <button onClick={testWithMockEvent} style={{ marginTop: '5px', marginRight: '5px' }}>
                            Test Mock Event
                        </button>
                        <button onClick={clearPWAData} style={{ marginTop: '5px' }}>
                            Clear PWA Data
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <Modal isOpen={showPrompt} toggle={() => setShowPrompt(false)}>
            <ModalHeader toggle={() => setShowPrompt(false)}>
                Install Our App
            </ModalHeader>
            <ModalBody>
                <p>Would you like to install our app for a better experience?</p>
                <p>You'll be able to:</p>
                <ul>
                    <li>Access the app offline</li>
                    <li>Get faster loading times</li>
                    <li>Receive push notifications</li>
                    <li>Use it like a native app</li>
                </ul>

                {/* Debug info in development */}
                {process.env.NODE_ENV === 'development' && (
                    <div style={{
                        marginTop: '15px',
                        padding: '10px',
                        background: '#f8f9fa',
                        borderRadius: '4px',
                        fontSize: '12px'
                    }}>
                        <strong>Debug Info:</strong><br/>
                        Has Prompt Method: {deferredPrompt && typeof deferredPrompt.prompt === 'function' ? 'Yes' : 'No'}<br/>
                        Event Type: {deferredPrompt ? deferredPrompt.constructor.name : 'None'}<br/>
                        Timestamp: {debugInfo.timestamp || 'N/A'}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleInstall}>
                    Install App
                </Button>
                <Button color="secondary" onClick={() => setShowPrompt(false)}>
                    Not Now
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default InstallPWAPrompt;